import { Injectable } from '@angular/core';
import { BehaviorSubject, distinctUntilChanged, Observable } from 'rxjs';
import { Stack } from '../utils/stack-utils';

export type LoaderType = 'full' | 'small' | 'inline';

export interface LoaderConfig {
  isLoading: boolean;
  type: LoaderType;
  dismissible: boolean;
  message: string;
}

export interface ShowConfig {
  type?: LoaderType;
  dismissible?: boolean;
  message?: string;
  safetyTimeoutMs?: number;
}

const DEFAULTS: { type: LoaderType; dismissible: boolean; message: string; safetyTimeoutMs: number; hideDelayMs: number } = {
  type: 'full',
  dismissible: false,
  message: 'Loading...',
  safetyTimeoutMs: 5000,
  hideDelayMs: 800,
};

const IDLE: Readonly<LoaderConfig> = {
  isLoading: false,
  type: DEFAULTS.type,
  dismissible: DEFAULTS.dismissible,
  message: DEFAULTS.message,
};

@Injectable({ providedIn: 'root' })
export class LoadingService {
  // Loader stays visible until every show() has a matching hide() — or the safetyTimeoutMs elapses.
  private readonly stack = new Stack<Omit<LoaderConfig, 'isLoading'>>();
  private firstShowTimestamp = 0;

  private safetyTimer: ReturnType<typeof setTimeout> | null = null;
  private hideDelayTimer: ReturnType<typeof setTimeout> | null = null;

  private readonly _state = new BehaviorSubject<LoaderConfig>(IDLE);

  readonly loading$: Observable<LoaderConfig> = this._state.pipe(
    distinctUntilChanged((a, b) =>
      a.isLoading === b.isLoading &&
      a.type === b.type &&
      a.message === b.message &&
      a.dismissible === b.dismissible
    )
  );

  show(typeOrConfig?: LoaderType | ShowConfig, dismissible = DEFAULTS.dismissible, message = DEFAULTS.message, safetyTimeoutMs = DEFAULTS.safetyTimeoutMs): void {
    const config = this.resolveConfig(typeOrConfig, dismissible, message, safetyTimeoutMs);

    clearTimeout(this.hideDelayTimer!);
    if (this.stack.isEmpty()) this.firstShowTimestamp = Date.now();

    this.stack.push({ type: config.type, dismissible: config.dismissible, message: config.message });
    this.emitActive();
    this.armSafetyTimer(config.safetyTimeoutMs);
  }

  hide(delayMs = DEFAULTS.hideDelayMs): void {
    if (this.stack.isEmpty()) return;

    this.stack.pop();
    if (!this.stack.isEmpty()) {
      this.emitActive(); // show the previous request's config
      return;
    }

    clearTimeout(this.safetyTimer!);
    this.safetyTimer = null;
    this.scheduleIdle(delayMs);
  }

  reset(): void {
    this.stack.clear();
    clearTimeout(this.safetyTimer!);
    clearTimeout(this.hideDelayTimer!);
    this.safetyTimer = null;
    this.hideDelayTimer = null;
    this._state.next(IDLE);
  }

  // ─── Private helpers ──────────────────────────────────────────────────────

  private resolveConfig(typeOrConfig: LoaderType | ShowConfig | undefined, dismissible: boolean, message: string, safetyTimeoutMs: number,): Required<ShowConfig> {
    if (typeof typeOrConfig === 'object' && typeOrConfig !== null) {
      return {
        type: typeOrConfig.type ?? DEFAULTS.type,
        dismissible: typeOrConfig.dismissible ?? DEFAULTS.dismissible,
        message: typeOrConfig.message ?? DEFAULTS.message,
        safetyTimeoutMs: typeOrConfig.safetyTimeoutMs ?? DEFAULTS.safetyTimeoutMs,
      };
    }
    return { type: typeOrConfig ?? DEFAULTS.type, dismissible, message, safetyTimeoutMs };
  }

  private emitActive(): void {
    this._state.next({ isLoading: true, ...this.stack.peek()! });
  }

  private scheduleIdle(delayMs: number): void {
    const elapsed = Date.now() - this.firstShowTimestamp;
    const wait = Math.max(0, delayMs - elapsed);

    this.hideDelayTimer = setTimeout(() => {
      this.hideDelayTimer = null;
      if (this.stack.isEmpty()) this._state.next(IDLE);
    }, wait);
  }

  private armSafetyTimer(timeoutMs: number): void {
    if (this.safetyTimer) return;
    this.safetyTimer = setTimeout(() => {
      this.safetyTimer = null;
      if (!this.stack.isEmpty()) {
        console.warn(`LoadingService: ${this.stack.size()} request(s) never called hide() — force-resetting after ${timeoutMs}ms.`);
        this.reset();
      }
    }, timeoutMs);
  }
}
